import { useState, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import {
  DataTable,
  DataTableColumn,
  FilterField,
} from '../../components/ui/data-table';
import { Badge } from '../../components/ui/badge';
import InlineFilter from '../../components/ui/inline-filter';
import PageHeader from '../../components/common/PageHeader';
import StatsCard from '../../components/common/StatsCard';
import { DrawerForm } from '../../components/ui/drawer-form';
import CreateIBForm from '../../components/ib/CreateIBForm';
import ActionDropdown from '../../components/common/action-dropdown';
import { getStatusColor } from '../../utils/helpers';
import {
  Plus,
  Download,
  Filter,
  Users,
  DollarSign,
  TrendingUp,
  UserCheck,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { IBData } from '../../components/ib/CreateIBForm';

interface IBAccount {
  id: string;
  name: string;
  email: string;
  referralCode: string;
  status: 'active' | 'inactive' | 'pending';
  tier: 'Master IB' | 'Sub IB';
  totalClients: number;
  totalCommission: string;
  parentIB?: string;
  createdAt: string;
}

const IBList = () => {
  const [searchValue, setSearchValue] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );
  const [showCreateIBDrawer, setShowCreateIBDrawer] = useState(false);
  const [isCreatingIB, setIsCreatingIB] = useState(false);

  const ibAccounts: IBAccount[] = [
    {
      id: '1',
      name: 'John Anderson',
      email: 'john.anderson@email.com',
      referralCode: 'IB-123456',
      status: 'active',
      tier: 'Master IB',
      totalClients: 45,
      totalCommission: '$12,450.00',
      createdAt: '2024-01-15',
    },
    {
      id: '2',
      name: 'Sarah Mitchell',
      email: 'sarah.m@email.com',
      referralCode: 'IB-234567',
      status: 'active',
      tier: 'Sub IB',
      totalClients: 23,
      totalCommission: '$5,890.50',
      parentIB: 'John Anderson',
      createdAt: '2024-02-20',
    },
    {
      id: '3',
      name: 'Michael Chen',
      email: 'michael.chen@email.com',
      referralCode: 'IB-345678',
      status: 'active',
      tier: 'Master IB',
      totalClients: 67,
      totalCommission: '$18,750.00',
      createdAt: '2023-12-05',
    },
    {
      id: '4',
      name: 'Emily Rodriguez',
      email: 'emily.r@email.com',
      referralCode: 'IB-456789',
      status: 'pending',
      tier: 'Sub IB',
      totalClients: 0,
      totalCommission: '$0.00',
      parentIB: 'Michael Chen',
      createdAt: '2024-03-10',
    },
    {
      id: '5',
      name: 'David Thompson',
      email: 'david.t@email.com',
      referralCode: 'IB-567890',
      status: 'active',
      tier: 'Sub IB',
      totalClients: 18,
      totalCommission: '$3,420.75',
      parentIB: 'John Anderson',
      createdAt: '2024-01-28',
    },
    {
      id: '6',
      name: 'Lisa Wang',
      email: 'lisa.wang@email.com',
      referralCode: 'IB-678901',
      status: 'inactive',
      tier: 'Master IB',
      totalClients: 12,
      totalCommission: '$2,890.00',
      createdAt: '2023-11-15',
    },
  ];

  const handleCreateIB = () => {
    setShowCreateIBDrawer(true);
  };

  const handleIBSubmit = async (data: IBData) => {
    setIsCreatingIB(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));

      const newIB: IBAccount = {
        id: (ibAccounts.length + 1).toString(),
        name: data.name,
        email: data.email,
        referralCode: `IB-${Date.now().toString().slice(-6)}`,
        status: data.status,
        tier: data.tier,
        totalClients: 0,
        totalCommission: '$0.00',
        parentIB: data.parentIB,
        createdAt: new Date().toISOString().split('T')[0],
      };
      console.log('New IB created:', newIB);

      setShowCreateIBDrawer(false);
      setIsCreatingIB(false);

      alert('IB account created successfully!');
    } catch (error) {
      console.error('Error creating IB:', error);
      setIsCreatingIB(false);
      alert('Error creating IB. Please try again.');
    }
  };

  const filterFields: FilterField[] = [
    {
      id: 'status',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'pending', label: 'Pending' },
      ],
    },
    {
      id: 'tier',
      label: 'Tier',
      options: [
        { value: 'Master IB', label: 'Master IB' },
        { value: 'Sub IB', label: 'Sub IB' },
      ],
    },
  ];

  const filteredIBAccounts = useMemo(() => {
    return ibAccounts.filter(ib => {
      if (activeFilters.status && ib.status !== activeFilters.status) {
        return false;
      }
      if (activeFilters.tier && ib.tier !== activeFilters.tier) {
        return false;
      }
      if (searchValue) {
        const searchLower = searchValue.toLowerCase();
        return (
          ib.name.toLowerCase().includes(searchLower) ||
          ib.email.toLowerCase().includes(searchLower) ||
          ib.referralCode.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [ibAccounts, activeFilters, searchValue]);

  const columns: DataTableColumn<IBAccount>[] = [
    {
      id: 'name',
      header: 'Name',
      accessor: row => (
        <div>
          <div className="font-medium">{row.name}</div>
        </div>
      ),
      sortable: true,
      sortValue: row => row.name,
      width: '120px',
    },
    {
      id: 'referralCode',
      header: 'Referral Code',
      accessor: row => (
        <code className="px-2 py-1 bg-muted rounded text-sm">
          {row.referralCode}
        </code>
      ),
      sortable: true,
      sortValue: row => row.referralCode,
      width: '150px',
    },
    {
      id: 'tier',
      header: 'Tier',
      accessor: row => (
        <Badge className={getStatusColor(row.tier, 'tier')}>{row.tier}</Badge>
      ),
      sortable: true,
      sortValue: row => row.tier,
      width: '120px',
    },
    {
      id: 'parentIB',
      header: 'Parent IB',
      accessor: row => row.parentIB || '-',
      sortable: true,
      sortValue: row => row.parentIB || '',
      width: '150px',
    },
    {
      id: 'totalClients',
      header: 'Total Clients',
      accessor: row => row.totalClients,
      sortable: true,
      sortValue: row => row.totalClients,
      width: '120px',
    },
    {
      id: 'totalCommission',
      header: 'Total Commission',
      accessor: row => row.totalCommission,
      sortable: true,
      sortValue: row => row.totalCommission,
      width: '150px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <Badge className={getStatusColor(row.status)}>{row.status}</Badge>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={row.id}
          size="sm"
          variant="ghost"
          showView={true}
          showEdit={true}
          showDelete={false}
          showDeposit={false}
          showWithdraw={false}
          onView={() => console.log('View IB:', row.id)}
          onEdit={() => console.log('Edit IB:', row.id)}
        />
      ),
      width: '120px',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="IB List"
          description="Manage and monitor IB accounts"
          actionLabel="Create New IB"
          actionIcon={Plus}
          onAction={handleCreateIB}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Total IBs"
          value={filteredIBAccounts.length.toString()}
          change="+0% from last month"
          icon={Users}
        />
        <StatsCard
          title="Active IBs"
          value={filteredIBAccounts
            .filter(ib => ib.status === 'active')
            .length.toString()}
          change="+0% from last month"
          icon={UserCheck}
        />
        <StatsCard
          title="Total Commission"
          value="$0.00"
          change="+0% from last month"
          icon={DollarSign}
        />
        <StatsCard
          title="New IBs"
          value="0"
          change="+0% from last month"
          icon={TrendingUp}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>IB Network</CardTitle>
              <CardDescription>Manage and view all IB accounts</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showFilters ? 'Hide Filter' : 'Show Filter'}
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <InlineFilter
            showFilters={showFilters}
            onToggle={() => setShowFilters(!showFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={() => setActiveFilters({})}
            onFiltersApply={() => {}}
            title="Filter IB Accounts"
          />
          <DataTable
            columns={columns}
            data={filteredIBAccounts}
            searchValue={searchValue}
            searchPlaceholder="Search IB accounts..."
            onSearchChange={setSearchValue}
            onSearchReset={() => setSearchValue('')}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFiltersChange={() => {}}
            showColumnVisibility={true}
            showAddButton={false}
            showExportButton={false}
            initialPageSize={10}
          />
        </CardContent>
      </Card>

      <DrawerForm<IBData>
        open={showCreateIBDrawer}
        onOpenChange={setShowCreateIBDrawer}
        title="Create New IB"
        description="Add a new Introducing Broker to your network"
        onSubmit={handleIBSubmit}
        submitText="Create IB"
        loading={isCreatingIB}
        width="lg"
      >
        <CreateIBForm onSubmit={handleIBSubmit} />
      </DrawerForm>
    </div>
  );
};

export default IBList;
